import React from 'react'

type SignedUrlEntry = { unsignedUrl: string; signedUrl: string | null }

export function SignedUrlContext({ signedUrls, children }: { signedUrls: SignedUrlEntry[]; children?: React.ReactNode }) {
  const existing = React.useContext(SignedUrlPreviewContext)
  const newUrls = React.useMemo(
    () => [...existing.filter((e) => !signedUrls.some((u) => u.unsignedUrl === e.unsignedUrl)), ...signedUrls],
    [existing, signedUrls]
  )

  return <SignedUrlPreviewContext.Provider value={newUrls}>{children}</SignedUrlPreviewContext.Provider>
}

export function useSignedUrl<T extends string | null | undefined, F extends string | null | undefined = T>(
  fileUrl: T,
  fallback: F = fileUrl as unknown as F
): string | F {
  const signedUrls = React.useContext(SignedUrlPreviewContext)
  const signedUrl = signedUrls.find((u) => u.unsignedUrl === fileUrl)?.signedUrl ?? fallback
  return signedUrl as F
}

/** True when the file is recorded but not in storage yet, so the page should say to wait. */
export function useIsSignedUrlPending(fileUrl: string | null | undefined) {
  const signedUrls = React.useContext(SignedUrlPreviewContext)
  return signedUrls.some((u) => u.unsignedUrl === fileUrl && !u.signedUrl)
}

export function useSignedUrlContext() {
  return React.useContext(SignedUrlPreviewContext)
}

const SignedUrlPreviewContext = React.createContext<SignedUrlEntry[]>([])
